import React, { useState, useEffect } from 'react';
import { LogoutOutlined } from '@mui/icons-material';
import { ChevronDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GlobalCommandPalette } from '@/components/GlobalCommandPalette';

interface TopbarProps {
  userName?: string;
  userAvatar?: string | null;
  onLogout: () => void;
  topBarColor?: string;
  onNavigate?: (id: string) => void;
}

export function Topbar({ 
  userName = 'Admin', 
  userAvatar = null, 
  onLogout,
  topBarColor = '#334238',
  onNavigate,
}: TopbarProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // ⌘K / Ctrl+K to open; Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleNavigate = (id: string) => {
    onNavigate?.(id);
    setIsSearchOpen(false);
  };

  return (
    <>
      <div 
        className="h-12 w-full border-b border-gray-700 px-1 flex items-center"
        style={{ backgroundColor: topBarColor }}
      >
        {/* Left Section - Logo/Title */}
        <div className="flex items-center gap-2 pl-3">
          <img
            src="https://storage.googleapis.com/ltp-static-data/lift_assets/lift-logo.png"
            alt="LIFT platform logo"
            className="w-[40px] h-[21px]"
          />
          <span className="text-xs leading-4 font-normal text-white/70 whitespace-nowrap">
            by New Engen
          </span>
        </div>

        {/* Center Section - Can be expanded later */}
        <div className="flex-1" />

        {/* Right Section - Search + User Menu */}
        <div className="flex-none flex items-center gap-3 pr-2">
          {/* Search trigger */}
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className="p-1.5 rounded-lg hover:bg-gray-700/50 transition-colors duration-150"
            title="Search (⌘K)"
          >
            <Search className="w-5 h-5 text-gray-300" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 px-2 py-1.5 h-auto rounded-lg hover:bg-gray-700/50 transition-colors duration-150"
              >
                <Avatar className="h-8 w-8">
                  {userAvatar ? (
                    <AvatarImage src={userAvatar} alt={userName} />
                  ) : null}
                  <AvatarFallback className="bg-gray-600 text-white text-xs">
                    {userName?.charAt(0)?.toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-gray-200 hidden sm:inline-block max-w-[120px] truncate">
                  {userName}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-56 bg-[#1a1a1a] border-gray-700"
            >
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-white">{userName}</p>
                <p className="text-xs text-gray-400">Administrator</p>
              </div>
              <DropdownMenuSeparator className="bg-gray-700" />
              <DropdownMenuItem 
                onClick={onLogout}
                className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-red-500/10 focus:bg-red-500/10 focus:text-red-300"
              >
                <LogoutOutlined className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Global Command Palette */}
      <GlobalCommandPalette
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        onNavigate={handleNavigate}
      />
    </>
  );
}

export default Topbar;

